import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import queryString from 'query-string';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_RESET_WITH_TOKEN,
  AUTH_RESTORE_PASSWORD,
  MAIL_SENT_RESET,
} from '../../redux/actions';
import { ButtonContainer } from './styles';
import { CTA, CustomInput, Logo, Text } from '../../../ui/atoms';
import {
  getValues,
  isValidForm,
  newForm,
  newFormField,
} from '../../../utils/form';
import { RegexpType, validator } from '../../../utils/validator';
import routes from '../../../routes';
import {
  AuthContainer,
  FormContainer,
  InputWrapper,
  LogoContainer,
} from '../Auth/styles';
import { Container } from '../../../ui/gridSystem';
import { withMediaQueries } from '../../hoc/withMediaQueries';

const initialMailForm = newForm([
  newFormField({
    field: 'mail',
    required: true,
    type: RegexpType.EMAIL,
    valid: true,
  }),
]);

const initialPwdForm = newForm([
  newFormField({
    field: 'new_password',
    required: true,
    type: RegexpType.RESET_PASSWORD,
    valid: true,
  }),
  newFormField({
    field: 'confirmPwd',
    required: true,
    type: RegexpType.PASSWORD,
    valid: true,
  }),
]);

const AuthReset = ({
  mediaIsPhone,
  sendMail,
  mailSent,
  resetPasswordWithToken,
  location,
  resetMailSent,
  appConfig,
}) => {
  const navigate = useNavigate();
  const { resetPasswordToken: token } = queryString.parse(location.search);
  const [mailForm, setMailForm] = useState(initialMailForm);
  const [pwdForm, setPwdForm] = useState(initialMailForm);

  useEffect(() => {
    window.scrollTo(0, 0);
    resetMailSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      setPwdForm(initialPwdForm);
    } else {
      setMailForm(initialMailForm);
    }
  }, [token]);

  const handleOnChangeMail = (newValue) => {
    const { type, required } = mailForm.mail;
    setMailForm({
      ...mailForm,
      mail: {
        ...mailForm.mail,
        value: newValue,
        valid:
          typeof newValue !== 'object'
            ? validator(type, newValue, required)
            : true,
      },
    });
  };

  const handleOnChange = (field = '', newValue) => {
    const { type, required } = pwdForm[field];
    setPwdForm({
      ...pwdForm,
      [field]: {
        ...pwdForm[field],
        value: newValue,
        valid:
          (typeof newValue !== 'object'
            ? validator(type, newValue, required)
            : true) &&
          (field === 'confirmPwd'
            ? pwdForm.new_password.value === newValue
            : true),
      },
    });
  };

  return (
    <AuthContainer>
      <FormContainer>
        <LogoContainer>
          <Logo
            src={appConfig?.login_logo?.src}
            alt={appConfig?.login_logo?.alt}
            size={Logo.SIZE.XL}
            onClick={() => navigate(routes.auth.path)}
          />
        </LogoContainer>
        <Container className="pt-0">
          <Text type="h1">Reset Password</Text>
          {!token ? (
            <>
              <InputWrapper>
                <Text type="h3" mb={8}>
                  Email
                </Text>
                <CustomInput
                  placeholder="email"
                  onChange={(newValue) => handleOnChangeMail(newValue)}
                  type="text"
                  value={mailForm?.mail?.value}
                  valid={mailForm?.mail?.valid}
                />
              </InputWrapper>
              <ButtonContainer>
                <CTA
                  fullsize={mediaIsPhone}
                  btnText="Conferma"
                  disabled={!isValidForm(mailForm) || mailSent}
                  onClick={() => sendMail({ email: mailForm?.mail?.value })}
                />
                {mailSent && (
                  <CTA
                    fullsize={mediaIsPhone}
                    type="secondary"
                    btnText="Torna alla login"
                    onClick={() => navigate(routes.auth.path)}
                  />
                )}
              </ButtonContainer>
            </>
          ) : (
            <>
              <InputWrapper>
                <Text type="h3" mb={8}>
                  Nuova password
                </Text>
                <CustomInput
                  placeholder="Nuova password"
                  onChange={(newValue) =>
                    handleOnChange('new_password', newValue)
                  }
                  type="password"
                  value={pwdForm?.new_password?.value || ''}
                  valid={pwdForm?.new_password?.valid}
                  error="La password deve contenere almeno 8 caratteri, inclusi 1 lettera maiuscola, 1 lettera minuscola, 1 numero e 1 carattere speciale"
                />
              </InputWrapper>
              <InputWrapper>
                <Text type="h3" mb={8}>
                  Conferma password
                </Text>
                <CustomInput
                  placeholder="Conferma password"
                  onChange={(newValue) =>
                    handleOnChange('confirmPwd', newValue)
                  }
                  type="password"
                  value={pwdForm?.confirmPwd?.value || ''}
                  valid={pwdForm?.confirmPwd?.valid}
                  error="Le password devono coincidere"
                />
              </InputWrapper>
              <ButtonContainer>
                <CTA
                  fullsize={mediaIsPhone}
                  btnText="Conferma"
                  disabled={!isValidForm(pwdForm)}
                  onClick={() =>
                    resetPasswordWithToken(
                      { ...getValues(pwdForm), token },
                      navigate
                    )
                  }
                />
              </ButtonContainer>
            </>
          )}
        </Container>
      </FormContainer>
    </AuthContainer>
  );
};

AuthReset.propTypes = {
  mediaIsPhone: PropTypes.bool,
  // HOC (connect, state)
  location: PropTypes.object.isRequired,
  mailSent: PropTypes.bool,
  appConfig: PropTypes.object,
  // HOC (connect, dispatch)
  sendMail: PropTypes.func.isRequired,
  resetPasswordWithToken: PropTypes.func.isRequired,
  resetMailSent: PropTypes.func,
};

export default connect(
  (state) => {
    const {
      authState: { mailSent },
      appConfig,
    } = state.app;
    const { location } = state.router;
    return { location, mailSent, appConfig };
  },
  (dispatch) => ({
    sendMail: (params) =>
      dispatch({ type: AUTH_RESTORE_PASSWORD._REQUEST, params }),
    resetPasswordWithToken: (params, navigate) =>
      dispatch({ type: AUTH_RESET_WITH_TOKEN._REQUEST, params, navigate }),
    resetMailSent: () => dispatch({ type: MAIL_SENT_RESET }),
  })
)(withMediaQueries(AuthReset));
